import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../../components/layout/Layout';
import Section from '../../../components/layout/Section';
import Container from '../../../components/layout/Container';

// UI Components
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';

/**
 * Video Tutorials page component
 */
const VideoTutorialsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('All');

  // Mock data for video tutorials
  const categories = [
    'All',
    'Getting Started',
    'Business Implementation',
    'Technical Deep Dives',
    'Case Studies',
    'Best Practices'
  ];

  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const videos = [
    {
      id: '1',
      title: 'AI Fundamentals for Business Leaders',
      description: 'A comprehensive introduction to AI concepts every business leader should understand before implementing AI solutions.',
      category: 'Getting Started',
      level: 'Beginner',
      duration: '45:30',
      views: '12.5k',
      thumbnail: '/api/placeholder/400/225',
      featured: true,
      date: '2024-01-15',
    },
    {
      id: '2',
      title: 'Building Your First Chatbot',
      description: 'Step-by-step tutorial on creating an intelligent chatbot for customer service using no-code tools.',
      category: 'Technical Deep Dives',
      level: 'Intermediate',
      duration: '1:12:45',
      views: '8.2k',
      thumbnail: '/api/placeholder/400/225',
      featured: false,
      date: '2024-01-22',
    },
    {
      id: '3',
      title: 'ROI Calculation for AI Projects',
      description: 'Learn how to calculate and demonstrate the return on investment for your AI initiatives.',
      category: 'Business Implementation',
      level: 'Intermediate',
      duration: '35:20',
      views: '15.8k',
      thumbnail: '/api/placeholder/400/225',
      featured: true,
      date: '2024-02-05',
    },
    {
      id: '4',
      title: 'Healthcare AI Implementation Case Study',
      description: 'Deep dive into how TechMed transformed their patient scheduling with AI, including challenges and solutions.',
      category: 'Case Studies',
      level: 'Advanced',
      duration: '58:15',
      views: '9.3k',
      thumbnail: '/api/placeholder/400/225',
      featured: false,
      date: '2024-02-12',
    },
    {
      id: '5',
      title: 'Data Privacy in AI Systems',
      description: 'Essential best practices for maintaining data privacy and compliance when implementing AI solutions.',
      category: 'Best Practices',
      level: 'Intermediate',
      duration: '42:30',
      views: '11.7k',
      thumbnail: '/api/placeholder/400/225',
      featured: true,
      date: '2024-02-18',
    },
    {
      id: '6',
      title: 'Workflow Automation Masterclass',
      description: 'Advanced techniques for automating complex business workflows using AI and machine learning.',
      category: 'Technical Deep Dives',
      level: 'Advanced',
      duration: '1:35:20',
      views: '6.4k',
      thumbnail: '/api/placeholder/400/225',
      featured: false,
      date: '2024-02-25',
    },
    {
      id: '7',
      title: 'AI Team Training Strategies',
      description: 'Proven approaches for training your team on AI tools and ensuring successful adoption.',
      category: 'Business Implementation',
      level: 'Beginner',
      duration: '38:45',
      views: '13.2k',
      thumbnail: '/api/placeholder/400/225',
      featured: false,
      date: '2024-03-03',
    },
    {
      id: '8',
      title: 'Machine Learning Model Deployment',
      description: 'Technical guide to deploying and monitoring machine learning models in production environments.',
      category: 'Technical Deep Dives',
      level: 'Advanced',
      duration: '1:21:10',
      views: '7.8k',
      thumbnail: '/api/placeholder/400/225',
      featured: false,
      date: '2024-03-10',
    },
  ];

  const filteredVideos = videos.filter(video => {
    const categoryMatch = selectedCategory === 'All' || video.category === selectedCategory;
    const levelMatch = selectedLevel === 'All' || video.level === selectedLevel;
    return categoryMatch && levelMatch;
  });

  const featuredVideos = videos.filter(video => video.featured);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner':
        return 'success';
      case 'Intermediate':
        return 'secondary';
      case 'Advanced':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <Section className="relative bg-gradient-to-br from-primary via-secondary to-accent overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
          <div className="absolute top-32 right-20 w-24 h-24 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-40 h-40 border border-white/10 rounded-full"></div>
        </div>
        
        <Container className="relative z-10 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Video 
              <span className="block bg-gradient-to-r from-cyan-300 to-white bg-clip-text text-transparent">
                Tutorials
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl mb-12 text-white/90 font-light"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Learn AI implementation through practical, 
              <br className="hidden md:block" />
              step-by-step video tutorials
            </motion.p>
          </div>
        </Container>
      </Section>

      {/* Featured Videos Section */}
      <Section className="py-20 bg-white">
        <Container>
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
              Featured Tutorials
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-3xl mx-auto">
              Start with our most popular tutorials, trusted by thousands of professionals
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <Card className="h-full bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  {/* Video Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    {/* Duration Badge */}
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-sm">
                      {video.duration}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant={getLevelColor(video.level)}>
                        {video.level}
                      </Badge>
                      <Badge variant="outline">Featured</Badge>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                      {video.title}
                    </h3>
                    
                    <p className="text-neutral-600 mb-4 leading-relaxed">
                      {video.description}
                    </p>
                    
                    {/* Metadata */}
                    <div className="flex items-center justify-between text-sm text-neutral-500 mb-4">
                      <span>{video.views} views</span>
                      <span>{video.category}</span>
                    </div>
                    
                    <Button 
                      variant="primary" 
                      fullWidth 
                      className="group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                      Watch Now
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* All Videos Section */}
      <Section className="py-20 bg-neutral-50">
        <Container>
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
              All Video Tutorials
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-3xl mx-auto">
              Browse our complete video library by category and skill level
            </p>
          </motion.div>

          {/* Filters */}
          <div className="mb-12">
            {/* Category Filter */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-neutral-800">Filter by Category</h3>
              <div className="flex flex-wrap gap-3">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className="transition-all duration-300"
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Level Filter */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-neutral-800">Filter by Level</h3>
              <div className="flex flex-wrap gap-3">
                {levels.map((level) => (
                  <Button
                    key={level}
                    variant={selectedLevel === level ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedLevel(level)}
                    className="transition-all duration-300"
                  >
                    {level}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Videos Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <Card className="h-full bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  {/* Video Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    {/* Duration Badge */}
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-sm">
                      {video.duration}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant={getLevelColor(video.level)}>
                        {video.level}
                      </Badge>
                      {video.featured && (
                        <Badge variant="outline">Featured</Badge>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors duration-300">
                      {video.title}
                    </h3>
                    
                    <p className="text-neutral-600 mb-4 leading-relaxed">
                      {video.description}
                    </p>
                    
                    {/* Metadata */}
                    <div className="flex items-center justify-between text-sm text-neutral-500 mb-4">
                      <span>{video.views} views</span>
                      <span>{video.category}</span>
                    </div>
                    
                    <Button 
                      variant="primary" 
                      fullWidth 
                      className="group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                      Watch Now
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* No Results Message */}
          {filteredVideos.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-neutral-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <p className="text-neutral-500 text-lg">No videos found matching your criteria.</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4"
                onClick={() => {
                  setSelectedCategory('All');
                  setSelectedLevel('All');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </Container>
      </Section>

      {/* CTA Section */}
      <Section className="py-20 bg-gradient-to-r from-neutral-900 to-neutral-800 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-4 h-4 bg-white rounded-full"></div>
          <div className="absolute top-20 right-20 w-3 h-3 bg-white rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-5 h-5 bg-white rounded-full"></div>
          <div className="absolute bottom-10 right-1/3 w-4 h-4 bg-white rounded-full"></div>
        </div>
        
        <Container className="relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready for 
                <span className="block md:inline text-transparent bg-gradient-to-r from-primary to-accent bg-clip-text">
                  {' '}Hands-On Training?
                </span>
              </h2>
              <p className="text-xl text-white/90 mb-10 leading-relaxed">
                Get personalized video tutorials and training sessions tailored to your specific 
                AI implementation needs.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  variant="primary"
                  size="lg" 
                  className="bg-gradient-to-r from-primary to-secondary hover:from-primary hover:to-secondary/90 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                  onClick={() => window.location.href = '/contact'}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Request Custom Training
                </Button>
                
                <Button 
                  variant="secondary"
                  size="lg"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-300"
                  onClick={() => window.location.href = '/resources'}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Explore All Resources
                </Button>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

export default VideoTutorialsPage;